import PropTypes, { bool, func } from "prop-types";

import CombinedCallout from "./CombinedCallout";
import StandardizationNotice from "./StandardizationNotice";
import UnverifiableCallout from "./UnverifiableCallout";

/**
 * The per-surface callout slot for inline surfaces (everything but the boards
 * popover, which owns its own slides). It renders the right callout for the
 * current verdict and — when BOTH the standardization-undo and unverifiable
 * states are true at once (the base-retain case) — merges them into a single
 * blue pill ([`CombinedCallout`](./CombinedCallout.jsx)) instead of stacking two
 * separate pills. All three are kept mounted with `open` toggles so the
 * transition between states fades rather than snaps.
 *
 * Left-justified by default: inline surfaces left-align their callouts (only the
 * boards popover centers, and it doesn't use this).
 */
export default function AddressCallouts({
  standardizationOpen,
  unverifiableOpen,
  onUndo,
  undoLoading,
  onDismissUnverifiable,
  flush,
  align = "left",
}) {
  const both = standardizationOpen && unverifiableOpen;
  return (
    <>
      <CombinedCallout
        open={both}
        onUndo={onUndo}
        loading={undoLoading}
        flush={flush}
        align={align}
      />
      <StandardizationNotice
        open={standardizationOpen && !both}
        onUndo={onUndo}
        loading={undoLoading}
        flush={flush}
        align={align}
      />
      <UnverifiableCallout
        open={unverifiableOpen && !both}
        onDismiss={onDismissUnverifiable}
        flush={flush}
        align={align}
      />
    </>
  );
}

AddressCallouts.propTypes = {
  // The standardization-undo state (a reformat just applied).
  standardizationOpen: bool,
  // The unverifiable verdict state.
  unverifiableOpen: bool,
  // Re-persist the typed address marked unverifiable.
  onUndo: func,
  // Undo POST in flight: spinner + disabled undo control.
  undoLoading: bool,
  // Opt-in dismiss (X) on the standalone unverifiable callout (admin profile).
  // The merged pill has no dismiss — undo is its only control.
  onDismissUnverifiable: func,
  // Drops the inline top/bottom margin (e.g. portaled placements).
  flush: bool,
  // "left" (default) | "center".
  align: PropTypes.oneOf(["left", "center"]),
};
